/**
 * Converts an object to a base64-encoded string, then creates
 * a Blob and triggers a download of that Blob as a file.
 *
 * @param data - The object to encode.
 * @param filename - The name of the downloaded file.
 */
export function downloadFile<T>(data: T, filename: string): void {

    const jsonString = JSON.stringify(data);
    const base64String = base64EncodeUnicode(jsonString);

    downloadBase64File(base64String, filename);
}

/**
 * Converts a base64-encoded string to a Blob, then creates
 * a Blob and triggers a download of that Blob as a file.
 *
 * @param base64 - The base64-encoded string to encode.
 * @param filename - The name of the downloaded file.
 */
export function downloadBase64File(base64: string, filename: string): void {

    const blob = new Blob([base64], { type: "text/plain" });
    const url = URL.createObjectURL(blob);

    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();


    URL.revokeObjectURL(url);
}

/**
 * Reads an obfuscated (base64-encoded) JSON file, decodes it, and returns the original object.
 * This function returns a Promise that resolves to the decoded JSON object.
 *
 * @param file - The file to decode
 * @returns A promise that resolves to the decoded object
 */
export function readFile<T>(file: File): Promise<T> {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader();

        fileReader.onload = () => {
            try {
                const base64Text = fileReader.result as string;
                const jsonString = base64DecodeUnicode(base64Text);
                const data = JSON.parse(jsonString);
                resolve(data);
            } catch (error) {
                reject(error);
            }
        };

        fileReader.onerror = (error) => {
            reject(error);
        };

        // 3) Read file as text, which in our example is base64-encoded JSON
        fileReader.readAsText(file);
    });
}

export function readBase64File(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader();

        fileReader.onload = () => {
            try {
                const base64Text = fileReader.result as string;
                resolve(base64Text);
            } catch (error) {
                reject(error);
            }
        };

        fileReader.onerror = (error) => {
            reject(error);
        };

        // 3) Read file as text, which in our example is base64-encoded JSON
        fileReader.readAsText(file);
    });
}


/**
 * Encode a Unicode string as base64 using the URI component trick.
 * @param str The string to encode
 */
function base64EncodeUnicode(str: string): string {
    // Step 1: percent-encode the string as UTF-8
    const encodedStr = encodeURIComponent(str);
    // Step 2: Each %xx escape is converted to its raw byte via fromCharCode
    const binary = encodedStr.replace(
        /%([0-9A-F]{2})/g,
        (match, p1) => String.fromCharCode(parseInt(p1, 16))
    );
    // Step 3: btoa the binary string
    return btoa(binary);
}

/**
 * Decode a base64 string (UTF-8) back into a Unicode string using the URI component trick.
 * @param base64 The base64-encoded text
 */
function base64DecodeUnicode(base64: string): string {
    // atob gives us a binary string
    const binary = atob(base64);
    // Convert that binary string into a %-escaped string
    const percentEncodedStr = binary
        .split("")
        .map(char => "%" + char.charCodeAt(0).toString(16).padStart(2, "0"))
        .join("");
    // Decode that to get back the original
    return decodeURIComponent(percentEncodedStr);
}